import { message, notification } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  endpointCreateTaxpayer,
  endpointTaxpayer,
  endpointTaxpayers,
  endpointUpdateTaxpayer,
} from "services/endpoints";

import { fnFetchData, fnFetchDataNewGateway } from "utils/requests";
import { removeFalseValueProperties } from "../utils/general";

import type { ItfTaxpayer } from "types/taxpayers";
import type { ItfResponseErrorHeru } from "../types/commons";
import type { UseQueryResult, QueryObserverOptions, QueryKey } from "react-query";

/* ---------------
--- QUERY KEYS ---
------------------*/
export const qkBaseTaxpayers = (): [string] => ["taxpayers"];

export const qkTaxpayers = (params?: { filters?: any }): QueryKey => {
  const { filters = undefined } = params || {};
  const baseListTaxpayers: string[] = [...qkBaseTaxpayers(), "list"];
  return filters ? [...baseListTaxpayers, filters] : baseListTaxpayers;
};

export const qkTaxpayer = ({ id }: { id: number }): QueryKey => {
  return [...qkBaseTaxpayers(), "taxpayer", id];
};

/* ------------------
--- DATA FETCHING ---
---------------------*/
export function useTaxpayers(props?: UseTaxpayersProps): UseQueryResult<any, any> {
  const { filters, options = {} }: UseTaxpayersProps = props;
  const filtersCleaned = removeFalseValueProperties({ obj: filters });
  const fn = () =>
    fnFetchDataNewGateway({
      config: { url: endpointTaxpayers({ filters: filtersCleaned }) },
    });
  return useQuery(qkTaxpayers({ filters: filtersCleaned }), fn, options);
}

export function useTaxpayer(props?: UseTaxpayerProps): UseQueryResult<any, any> {
  const { taxpayerId, options = {} } = props || {};
  const fn = () => fnFetchData({ config: { url: endpointTaxpayer({ taxpayerId }) } });
  return useQuery(qkTaxpayer({ id: taxpayerId }), fn, options);
}

export function useCreateTaxpayer(props?: UseCreateTaxpayerProps) {
  const { options = {} } = props || {};
  const queryClient = useQueryClient();
  const fn = ({ body = {} }: { body: Partial<ItfTaxpayer> }) =>
    fnFetchData({ config: { data: body, method: "POST", url: endpointCreateTaxpayer() } });
  const rqOpts: QueryObserverOptions = {
    onSuccess: (): void => {
      queryClient.invalidateQueries(qkBaseTaxpayers()).then();
      notification.success({ duration: 5, message: "Creación exitosa" });
    },
    onError: (rError: ItfResponseErrorHeru): void => {
      message.error({ content: JSON.stringify(rError.message) }).then();
    },
  };
  return useMutation(fn, Object.assign(rqOpts, options));
}

export function useUpdateTaxpayer(props?: UseUpdateTaxpayerProps) {
  const { options = {} } = props || {};
  const fn = ({ taxpayerId, body = {} }: { taxpayerId: number; body?: ItfTaxpayer | {} }) =>
    fnFetchData({ config: { data: body, method: "PUT", url: endpointUpdateTaxpayer({ taxpayerId }) } });
  const rqOpts: QueryObserverOptions = {
    onError: (rError: ItfResponseErrorHeru | any): void => {
      message.error({ content: JSON.stringify(rError.message) }).then();
    },
  };
  return useMutation(fn, Object.assign(rqOpts, options));
}

/* ------------------
--- INTERNAL TYPES ---
---------------------*/
export interface FiltersTaxpayers {
  page: number;
  limit: number;
  skid?: string;
  rfc?: string;
  name?: string;
  email?: string;
  phone_number?: string;
}

interface UseTaxpayersProps {
  filters: FiltersTaxpayers;
  options?: QueryObserverOptions | {};
}

interface UseTaxpayerProps {
  taxpayerId: number;
  options?: QueryObserverOptions | {};
}

interface UseCreateTaxpayerProps {
  options?: QueryObserverOptions | {};
}

interface UseUpdateTaxpayerProps {
  options?: QueryObserverOptions | {};
}
